/* ============================================================
   print.js — draws the product live, in the browser.

   Pairs with assets/css/print.css. Between them they reproduce the four
   templates under templates/ so the customiser can show a preview
   that answers every choice instantly, rather than a flat JPEG that can
   only answer the choices we happened to pre-render.

   The poster is laid out at the templates' own base canvas (1754x1240 CSS
   px) and then scaled to fit its container. Rendering at the true size and
   scaling afterwards is what keeps every percentage resolving against the
   same box it resolves against in render.mjs.
   ============================================================ */
(function (W) {
  'use strict';

  const BASE_W = 1754, BASE_H = 1240;

  /* Real paper sizes, for the to-scale room view. Outer size includes a
     standard 20mm moulding when a frame is chosen. */
  const PAPER_MM = { A4: [297, 210], A3: [420, 297], A2: [594, 420] };
  const MOULDING_MM = { none: 0, black: 20, oak: 24 };

  const el = (tag, cls, html) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  };
  const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* ------------------------------------------------------------------
     The nameplate auto-shrink.
     render.mjs measures the plaque and steps --name-size down until the
     name fits. The same has to happen here or a long name silently
     overflows its plaque — and flex items default to min-width:auto, so
     it would widen the column and squeeze the photo (DESIGN.md §3).
     ------------------------------------------------------------------ */
  function fitText(span, plate, max, min) {
    let size = max;
    span.style.fontSize = size + 'px';
    /* the plate has padding; measure against its content box */
    const room = plate.clientWidth * 0.88;
    let guard = 40;
    while (span.scrollWidth > room && size > min && guard--) {
      size -= Math.max(1, Math.round(size * 0.06));
      span.style.fontSize = size + 'px';
    }
    return size;
  }

  /* ------------------------------------------------------------------
     Markup per style
     ------------------------------------------------------------------ */
  function buildClassic(p) {
    const photo = p.photo
      ? `<img src="${esc(p.photo)}" alt="">`
      : `<div class="empty">PHOTO</div>`;
    const sig = p.signature ? `<img src="${esc(p.signature)}" alt="">` : '';
    return `
      <div class="poster__mount">
        <div class="poster__panel poster__photo">${photo}</div>
        <div class="poster__col">
          <div class="poster__panel poster__metal poster__sig">${sig}</div>
          <div class="poster__spacer"></div>
          <div class="poster__panel poster__metal poster__name"><span></span></div>
        </div>
      </div>
      <div class="poster__dedication"><div class="plate"><span></span></div></div>`;
  }

  function buildPatch(p) {
    const photo = p.photo
      ? `<img src="${esc(p.photo)}" alt="">`
      : `<div class="empty">PHOTO</div>`;
    const sig = p.signature ? `<img src="${esc(p.signature)}" alt="">` : '';
    return `
      <div class="poster__mount">
        <div class="poster__panel poster__photo">${photo}</div>
        <div class="poster__col">
          <div class="poster__panel poster__patch">
            <div class="weave"></div><div class="sash"></div><div class="stitch"></div>
            <div class="badge">${esc(p.club)}<small>${esc(p.number || '')}</small></div>
          </div>
          <div class="poster__spacer"></div>
          <div class="poster__panel poster__metal poster__sig">${sig}</div>
          <div class="poster__spacer"></div>
          <div class="poster__panel poster__metal poster__name"><span></span></div>
        </div>
      </div>
      <div class="poster__dedication"><div class="plate"><span></span></div></div>`;
  }

  function buildMuseum(p) {
    const photo = p.photo ? `<img src="${esc(p.photo)}" alt="">` : `<div class="empty">PHOTO</div>`;
    const sig = p.signature ? `<img src="${esc(p.signature)}" alt="">` : '';
    return `
      <div class="poster__mount">
        <div class="poster__museum-photo">${photo}</div>
        <div class="poster__info">
          <div class="club">${esc(p.club)}</div>
          <div class="player"></div>
          <div class="rule"></div>
          <div class="autograph">${sig}</div>
          <div class="footnote">Pre-printed autograph &middot; 300gsm</div>
        </div>
      </div>
      <div class="poster__dedication"><div class="plate"><span></span></div></div>`;
  }

  const BUILDERS = {
    'style-01-classic': buildClassic,
    'style-02-gold':    buildClassic,
    'style-03-patch':   buildPatch,
    'style-04-museum':  buildMuseum,
  };

  /* ------------------------------------------------------------------
     Poster instance
     ------------------------------------------------------------------ */
  function Poster(host, opts = {}) {
    const stage  = el('div', 'poster-stage');
    const poster = el('div', 'poster');
    stage.appendChild(poster);
    host.appendChild(stage);

    let state = {};
    let builtFor = null;

    function scale() {
      const w = stage.clientWidth;
      if (!w) return;
      poster.style.transform = `scale(${w / BASE_W})`;
    }

    /* Scaling must happen after layout, and again whenever the container
       changes. ResizeObserver rather than a resize listener, because the
       customiser column changes width without the window doing so. */
    const ro = new ResizeObserver(scale);
    ro.observe(stage);

    function render(next) {
      state = Object.assign({}, state, next);
      const p = state.product;
      if (!p) return;

      const styleId = state.style || p.styles[0] || 'style-01-classic';
      const key = styleId + '|' + p.slug;

      if (key !== builtFor) {
        poster.dataset.style = styleId;
        poster.innerHTML = (BUILDERS[styleId] || buildClassic)({
          photo: p.live && p.live.photo ? opts.base + p.live.photo : null,
          signature: p.live && p.live.signature ? opts.base + p.live.signature : null,
          club: p.club, number: p.number,
        });
        builtFor = key;
      }

      /* per-product colour treatment, exactly as the CSV drives it */
      poster.style.setProperty('--look-sat', p.look.sat);
      poster.style.setProperty('--look-con', p.look.con);
      poster.style.setProperty('--look-bri', p.look.bri);
      poster.style.setProperty('--club-primary', p.colours.primary);
      poster.style.setProperty('--club-secondary', p.colours.secondary);
      poster.style.setProperty('--club-ink', p.colours.ink);

      /* the name — plaque on three styles, set type on Museum */
      const nameSpan = poster.querySelector('.poster__name span');
      const player   = poster.querySelector('.poster__info .player');
      const shown    = (state.nameOverride || p.name).toUpperCase();
      if (nameSpan) {
        nameSpan.textContent = shown;
        const plate = nameSpan.closest('.poster__name');
        fitText(nameSpan, plate, styleId === 'style-03-patch' ? 65 : 88, 22);
      }
      if (player) player.textContent = state.nameOverride || p.name;

      /* the dedication engraved into the bottom margin */
      const ded = poster.querySelector('.poster__dedication span');
      const text = (state.dedication || '').trim();
      if (ded) {
        ded.textContent = text;
        if (text) {
          poster.setAttribute('data-dedication', '');
          fitText(ded, ded.closest('.plate'), styleId === 'style-04-museum' ? 16 : 39, 9);
        } else {
          poster.removeAttribute('data-dedication');
        }
      }
      scale();
    }

    return {
      el: stage,
      render,
      destroy() { ro.disconnect(); host.removeChild(stage); },
    };
  }

  /* ------------------------------------------------------------------
     Room view — the print on a wall, at true physical scale.
     ------------------------------------------------------------------ */
  const ROOMS = {
    'living-room': { cx: 44, cy: 36, wallMm: 2300, name: 'Living room' },
    'bedroom':     { cx: 52, cy: 28, wallMm: 1960, name: 'Bedroom' },
    'office':      { cx: 47, cy: 30, wallMm: 2000, name: 'Office' },
    'games-room':  { cx: 48, cy: 32, wallMm: 2200, name: 'Games room' },
  };

  function Room(host, opts = {}) {
    const wrap = el('div', 'room');
    const wall = el('img', 'wall');
    wall.alt = '';
    const hang = el('div', 'room__hang');
    const framed = el('div', 'framed');
    hang.appendChild(framed);
    wrap.append(wall, hang);
    host.appendChild(wrap);

    const poster = Poster(framed, opts);
    let roomId = opts.room || 'living-room';

    function render(next) {
      const st = next || {};
      if (st.room && ROOMS[st.room]) roomId = st.room;
      const r = ROOMS[roomId];
      /* Shopify's asset store is flat, so the theme passes a prefix instead
         of a folder. Everywhere else this resolves to the same path. */
      wall.src = opts.base + (opts.roomPrefix || 'img/room/wall-') + roomId + '.jpg';

      const [pw] = PAPER_MM[st.size || 'A4'];
      const frame = st.frame || 'none';
      const outerMm = pw + 2 * MOULDING_MM[frame];

      /* The print's share of the wall is a ratio of real millimetres —
         never a guessed percentage. */
      hang.style.width = (outerMm / r.wallMm * 100) + '%';
      hang.style.left = r.cx + '%';
      hang.style.top = r.cy + '%';
      framed.className = 'framed framed--' + frame;
      framed.style.setProperty('--moulding', frame === 'none' ? '0%'
        : (MOULDING_MM[frame] / outerMm * 100) + '%');

      poster.render(st);
    }

    return { el: wrap, render, rooms: ROOMS };
  }

  W.WSPPrint = { Poster, Room, PAPER_MM, MOULDING_MM, ROOMS };
})(window);
